import { theme } from 'antd';
import { EditIcon, TeamIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { teamsActions } from 'reduxStore/teams/slice';
import { Avatar, Button, Flex, Input, Tooltip, Typography } from 'zui/Antd';

const { Text } = Typography;
const { TextArea } = Input;

const EditDrawerHeader = ({ teamID, name, description }) => {
	const canEditTeam = useManagePermittedRoles('updateTeam');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const formColRef = useRef(null);

	const {
		token: {
			paddingLG,
			fontSizeHeading3,
			fontSizeHeading5,
			colorText,
			colorTextDescription,
			colorTextLabel,
			fontWeightStrong,
			fontSizeSM,
			colorError
		}
	} = theme.useToken();

	const [editable, setEditable] = useState(false);
	const [updatedName, setUpdatedName] = useState(name);
	const [updatedDesc, setUpdatedDesc] = useState(description);
	const [errors, setErrors] = useState({ name: '', description: '' });
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		setUpdatedName(name);
		setUpdatedDesc(description);
		setErrors({ name: '', description: '' });
		setEditable(false);
	}, [teamID, name, description]);

	useEffect(() => {
		if (editable && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editable]);

	const handleFinish = useCallback(() => {
		const trimmedName = updatedName?.trim();
		const trimmedDesc = updatedDesc?.trim();
		const newErrors = { name: '', description: '' };

		if (!trimmedName) newErrors.name = 'Name is required';
		if (!trimmedDesc) newErrors.description = 'Description is required';

		setErrors(newErrors);
		if (newErrors.name || newErrors.description) return;

		if (trimmedName !== name.trim() || trimmedDesc !== description.trim()) {
			dispatch(
				teamsActions.updateTeam({
					teamID,
					data: {
						name: trimmedName,
						description: trimmedDesc
					}
				})
			);
		}

		setEditable(false);
	}, [dispatch, teamID, updatedName, updatedDesc, name, description]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			const clickedOutside = formColRef.current && !formColRef.current.contains(event.target);

			if (editable && clickedOutside) {
				handleFinish();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [editable, handleFinish]);

	return (
		<Flex gap={paddingLG}>
			<Avatar
				size={54}
				icon={<TeamIcon color='#FFF' width={41} height={41} />}
				style={{ backgroundColor: '#42A8A8', alignSelf: 'start', marginTop: 6 }}
				shape='square'
			/>

			<Flex ref={formColRef} vertical flex='1'>
				{editable ? (
					<>
						<Input
							ref={inputRef}
							value={updatedName}
							onChange={(e) => setUpdatedName(e.target.value)}
							onPressEnter={handleFinish}
							style={{
								fontSize: fontSizeHeading3,
								fontWeight: fontWeightStrong,
								lineHeight: '32px',
								padding: 0
							}}
						/>
						{errors.name && (
							<Text style={{ color: colorError, fontSize: fontSizeSM, fontWeight: fontWeightStrong }}>
								{errors.name}
							</Text>
						)}
					</>
				) : (
					<Text
						style={{
							fontSize: fontSizeHeading3,
							fontWeight: fontWeightStrong,
							lineHeight: '32px',
							color: colorText
						}}
					>
						{name}
					</Text>
				)}

				{editable ? (
					<>
						<TextArea
							value={updatedDesc}
							onChange={(e) => setUpdatedDesc(e.target.value)}
							onPressEnter={handleFinish}
							style={{
								fontSize: fontSizeHeading5,
								fontWeight: fontWeightStrong,
								color: colorTextDescription,
								lineHeight: '24px',
								paddingLeft: 0
							}}
							maxLength={150}
							showCount={false}
						/>
						{errors.description && (
							<Text style={{ color: colorError, fontSize: fontSizeSM, fontWeight: fontWeightStrong }}>
								{errors.description}
							</Text>
						)}
						<div style={{ textAlign: 'right', marginTop: 4 }}>
							<Text
								type={updatedDesc.length >= 150 ? 'danger' : 'secondary'}
								style={{ fontSize: fontSizeSM }}
							>
								{updatedDesc.length}/150
								{updatedDesc.length === 150 && ' - Character limit reached'}
							</Text>
						</div>
					</>
				) : (
					<Flex align='center' gap={6} wrap='wrap'>
						<Text
							style={{
								fontSize: fontSizeHeading5,
								fontWeight: fontWeightStrong,
								color: colorTextLabel,
								lineHeight: '24px',
								marginBottom: 0
							}}
						>
							{isExpanded
								? updatedDesc
								: `${updatedDesc.slice(0, 70)}${updatedDesc.length > 70 ? '...' : ''}`}
							{updatedDesc.length > 70 && (
								<Button
									type='link'
									size='small'
									style={{ padding: 0, fontSize: fontSizeSM, height: 'auto', marginLeft: 6 }}
									onClick={() => setIsExpanded((prev) => !prev)}
								>
									{isExpanded ? 'Collapse' : 'Expand'}
								</Button>
							)}
						</Text>
					</Flex>
				)}
			</Flex>

			<Tooltip title={!canEditTeam ? permissionDeniedMessage : ''}>
				<Button
					onClick={() => canEditTeam && setEditable((prev) => !prev)}
					type='text'
					size='small'
					disabled={!canEditTeam}
					icon={<EditIcon color='currentColor' />}
					style={{ alignSelf: 'start' }}
				/>
			</Tooltip>
		</Flex>
	);
};

export default EditDrawerHeader;
